import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentJointDocumentVerificationCompleteData,
  type EnrollmentJointDocumentVerificationCompleteSubmitInput,
  type EnrollmentJointDocumentVerificationCompleteSubmitOutput,
} from '../../../../models'
import { createMutationHook, createQueryHook } from '../../../../hooks'

/**
 * Get Enrollment Joint DocumentVerificationComplete data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentJointDocumentVerificationCompleteData>
 * @example const response = await getEnrollmentJointDocumentVerificationCompleteData()
 */
export const getEnrollmentJointDocumentVerificationCompleteData = async (
  context?: DefaultContext,
): Promise<EnrollmentJointDocumentVerificationCompleteData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentJointDocumentVerificationCompleteData',
      query: `mutation getEnrollmentJointDocumentVerificationCompleteData($input: SubmitJointEnrollmentInput!) {
        submitJointEnrollment(input: $input) {
          errors
          isError
          activePerson {
            path
            progress
            name {
              firstName
              lastName
            }
          }
        }
      }`,
    },
    {
      input: {},
    },
    context,
  )) as { submitJointEnrollment: EnrollmentJointDocumentVerificationCompleteData }
  return response.submitJointEnrollment
}

export const useGetEnrollmentJointDocumentVerificationCompleteData = createQueryHook<
  undefined,
  EnrollmentJointDocumentVerificationCompleteData
>(getEnrollmentJointDocumentVerificationCompleteData)

/**
 * Submit Enrollment Joint DocumentVerificationComplete data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentJointDocumentVerificationCompleteSubmitOutput>
 * @example const response = await submitEnrollmentJointDocumentVerificationCompleteData();
 */
export const submitEnrollmentJointDocumentVerificationCompleteData = async (
  enrollmentDocumentVerificationCompleteSubmitInput: EnrollmentJointDocumentVerificationCompleteSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentJointDocumentVerificationCompleteSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentJointDocumentVerificationCompleteData',
      query: `mutation submitEnrollmentJointDocumentVerificationCompleteData($input: SubmitJointEnrollmentInput!) {
        submitJointEnrollment(input: $input) {
          errors
          isError
          activePerson {
            path
          }
        }
      }`,
    },
    {
      input: enrollmentDocumentVerificationCompleteSubmitInput,
    },
    context,
  )) as { submitJointEnrollment: EnrollmentJointDocumentVerificationCompleteSubmitOutput }
  return response.submitJointEnrollment
}

export const useSubmitEnrollmentJointDocumentVerificationCompleteData = createMutationHook<
  EnrollmentJointDocumentVerificationCompleteSubmitInput,
  EnrollmentJointDocumentVerificationCompleteSubmitOutput
>(submitEnrollmentJointDocumentVerificationCompleteData)
